import React, { useState, useEffect } from 'react';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';
import { 
    FaBriefcase, FaUser, FaClock, FaCheckCircle, FaPlay, 
    FaFlagCheckered, FaHourglassHalf, FaMoneyBillWave, FaUsers, FaMapMarkerAlt, FaStar 
} from 'react-icons/fa';
import { confirmJobStart, confirmJobCompletion } from '../api/jobs';
import { Link } from 'react-router-dom';
import RatingModal from '../components/RatingModal'; // Assuming we extract RatingModal to a component

const MyJobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, open, active, completed
  const [error, setError] = useState(null);

  // Rating State
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [ratingData, setRatingData] = useState({ workerId: '', workerName: '', hireId: '' });

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/jobs/my-jobs');
      console.log("[Debug] My Jobs Data:", response.data);
      setJobs(response.data);
      setFilteredJobs(response.data);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError("Failed to load jobs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (!jobs) return;
    if (filter === 'all') {
        setFilteredJobs(jobs);
    } else {
        setFilteredJobs(jobs.filter(job => {
            if (!job) return false;
            const contract = job.direct_hires?.[0];
            const status = contract ? contract.status : job.status;
            if (filter === 'open') return status === 'open';
            if (filter === 'active') return ['running', 'active', 'pending_start', 'pending_end'].includes(status);
            if (filter === 'completed') return status === 'completed';
            return true;
        }));
    }
  }, [filter, jobs]);

  const handleConfirmStart = async (jobId) => {
    if (!window.confirm("Are you sure you want to confirm the start of this job?")) return;
    try {
      await confirmJobStart(jobId);
      fetchJobs(); 
    } catch (err) {
      alert(err.response?.data?.message || "Failed to confirm start.");
    }
  };

  const handleConfirmCompletion = async (jobId) => {
    if (!window.confirm("Are you sure you want to confirm completion and pay?")) return;
    try {
      await confirmJobCompletion(jobId);
      fetchJobs(); 
    } catch (err) {
      alert(err.response?.data?.message || "Failed to confirm completion.");
    }
  };

  const TabButton = ({ name, label, count }) => (
      <button 
        onClick={() => setFilter(name)}
        className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${filter === name ? 'bg-slate-800 text-white shadow-md' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
      >
          {label} {count !== undefined && <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${filter === name ? 'bg-slate-600 text-white' : 'bg-slate-200 text-slate-600'}`}>{count}</span>}
      </button>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 font-sans">
      
      {/* Header & Tabs */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
        <div>
            <h1 className="text-3xl font-black text-slate-800 tracking-tight">My Jobs</h1>
            <p className="text-slate-500 mt-1">Manage your postings and hires.</p>
        </div>
        <div className="flex flex-wrap gap-2">
            <TabButton name="all" label="All Jobs" count={jobs.length} />
            <TabButton name="open" label="Open" />
            <TabButton name="active" label="Active" />
            <TabButton name="completed" label="Completed" />
        </div>
      </div>

      {loading ? (
          <div className="space-y-4 animate-pulse">
              {[1,2,3].map(i => <div key={i} className="h-40 bg-slate-100 rounded-2xl"></div>)}
          </div>
      ) : error ? (
          <div className="p-8 text-center bg-red-50 text-red-600 rounded-2xl font-medium">{error}</div>
      ) : filteredJobs.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-[2rem] border border-dashed border-slate-200 shadow-sm">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                <FaBriefcase className="text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-slate-700">No jobs found</h3>
            <p className="text-slate-500 mt-1">Try changing the filter or post a new job.</p>
            <Link to="/publish-job" className="mt-6 inline-block bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all">
                Post a Job
            </Link>
          </div>
      ) : (
          <div className="space-y-6">
            {filteredJobs.map(job => {
                if (!job) return null;
                // Identify all real hires
                const allHired = job.direct_hires?.filter(h => h && h.worker_id !== null) || [];
                const hireCount = allHired.length;
                
                // For the single-worker view, use the first one
                const contract = allHired[0] || job.direct_hires?.[0];
                const worker = contract?.hired_worker;
                const status = contract ? contract.status : (job.status || 'open');
                const jobDetails = job.details?.[0] || {};
                
                const isActionRequired = status === 'pending_start' || status === 'pending_end';

                return (
                    <div key={job.id} className={`bg-white rounded-[1.5rem] p-6 md:p-8 shadow-sm border transition-all hover:shadow-md ${isActionRequired ? 'border-l-8 border-l-purple-500 border-t border-r border-b border-slate-100' : 'border border-slate-100'}`}>
                        <div className="flex flex-col lg:flex-row gap-8">
                            
                            {/* Left: Job Info */}
                            <div className="flex-grow">
                                <div className="flex items-center gap-3 mb-3">
                                    <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${
                                        status === 'open' ? 'bg-green-100 text-green-700' :
                                        status === 'running' || status === 'active' ? 'bg-blue-100 text-blue-700' :
                                        status === 'completed' ? 'bg-slate-100 text-slate-600' :
                                        'bg-purple-100 text-purple-700'
                                    }`}>
                                        {status.replace('_', ' ')}
                                    </span>
                                    <span className="text-slate-400 text-xs font-bold flex items-center">
                                        <FaClock className="mr-1" /> {new Date(job.createdAt).toLocaleDateString()}
                                    </span>
                                </div>
                                <h2 className="text-xl font-black text-slate-800 mb-2 leading-tight">{job.title}</h2>
                                <p className="text-slate-500 text-sm mb-6 line-clamp-2 leading-relaxed">{job.description}</p>
                                
                                <div className="flex flex-wrap gap-4 md:gap-8">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><FaMoneyBillWave /></div>
                                        <div>
                                            <p className="text-xs text-slate-400 font-bold uppercase">Budget</p>
                                            <p className="text-sm font-bold text-slate-700">৳{jobDetails.budget_per_worker || 0}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><FaUsers /></div>
                                        <div>
                                            <p className="text-xs text-slate-400 font-bold uppercase">Applicants</p>
                                            <p className="text-sm font-bold text-slate-700">{job.applicants?.length || 0}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right: Worker / Action Card */}
                            <div className="w-full lg:w-80 flex-shrink-0">
                                {hireCount > 1 ? (
                                    /* Multi-Worker View */
                                    <div className="bg-emerald-900 text-white rounded-2xl p-6 h-full flex flex-col justify-between shadow-xl shadow-emerald-900/20">
                                        <div>
                                            <div className="flex -space-x-3 mb-4">
                                                {allHired.slice(0, 4).map((h, idx) => (
                                                    <img key={idx} src={loadImage(h.hired_worker?.picture)} className="w-10 h-10 rounded-full border-2 border-emerald-900 object-cover" alt="Team" />
                                                ))}
                                                {hireCount > 4 && (
                                                    <div className="w-10 h-10 rounded-full border-2 border-emerald-900 bg-emerald-700 flex items-center justify-center text-[10px] font-bold">
                                                        +{hireCount - 4}
                                                    </div>
                                                )}
                                            </div>
                                            <p className="text-xs font-bold text-emerald-300 uppercase tracking-widest mb-1">Team Managed</p>
                                            <p className="text-lg font-black">{hireCount} Professionals Hired</p>
                                        </div>
                                        
                                        <Link 
                                            to={`/manage-job/${job.id}`} 
                                            className="w-full py-3 mt-6 bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl font-bold text-center transition-all shadow-lg shadow-emerald-900/40"
                                        >
                                            Manage Team
                                        </Link>
                                    </div>
                                ) : worker ? (
                                    /* Single Worker View (Existing) */
                                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 h-full flex flex-col justify-between">
                                        <div className="flex items-center gap-3 mb-4">
                                            <img src={loadImage(worker.picture)} alt={worker.name} className="w-12 h-12 rounded-xl object-cover shadow-sm bg-white" />
                                            <div>
                                                <p className="text-xs text-slate-400 font-bold uppercase">Hired Worker</p>
                                                <p className="text-sm font-black text-slate-800">{worker.name}</p>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        {status === 'pending_start' && (
                                            <button onClick={() => handleConfirmStart(job.id)} className="w-full py-3 bg-purple-600 text-white rounded-xl font-bold shadow-lg shadow-purple-200 hover:bg-purple-700 transition-all flex items-center justify-center gap-2">
                                                <FaPlay size={12} /> Confirm Start
                                            </button>
                                        )}
                                        {status === 'active' && (
                                            <div className="w-full py-3 bg-white border border-blue-200 text-blue-600 rounded-xl font-bold flex items-center justify-center gap-2">
                                                <FaPlay className="animate-pulse" size={10} /> In Progress
                                            </div>
                                        )}
                                        {status === 'pending_end' && (
                                            <button onClick={() => handleConfirmCompletion(job.id)} className="w-full py-3 bg-orange-500 text-white rounded-xl font-bold shadow-lg shadow-orange-200 hover:bg-orange-600 transition-all flex items-center justify-center gap-2">
                                                <FaCheckCircle size={14} /> Confirm & Pay
                                            </button>
                                        )}
                                        {/* Track Live for Buyer */}
                                        {['active', 'running'].includes(status) && (
                                            <Link 
                                                to={`/track-worker/${contract.id}`} 
                                                className="w-full py-3 mt-2 bg-blue-100 text-blue-700 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-200 transition-all"
                                            >
                                                <FaMapMarkerAlt size={12} /> Track Live
                                            </Link>
                                        )}
                                        {status === 'completed' && (
                                            <div className="flex flex-col gap-2">
                                                <div className="w-full py-3 bg-green-100 text-green-700 rounded-xl font-bold flex items-center justify-center gap-2">
                                                    <FaFlagCheckered /> Job Completed
                                                </div>
                                                {!contract.rating ? (
                                                    <button 
                                                        onClick={() => {
                                                            setRatingData({ 
                                                                workerId: worker.id, 
                                                                workerName: worker.name,
                                                                hireId: contract.id 
                                                            });
                                                            setIsRatingModalOpen(true);
                                                        }}
                                                        className="w-full py-3 bg-yellow-500 text-white rounded-xl font-bold hover:bg-yellow-600 transition-all flex items-center justify-center gap-2 shadow-lg shadow-yellow-100"
                                                    >
                                                        <FaStar /> Rate Professional
                                                    </button>
                                                ) : (
                                                    <div className="text-center pt-1">
                                                        <div className="flex justify-center text-yellow-400 gap-0.5">
                                                            {[...Array(contract.rating.rating)].map((_, i) => <FaStar key={i} size={10} />)}
                                                        </div>
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Feedback Given</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    /* Empty View */
                                    <div className="h-full flex flex-col items-center justify-center p-6 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-center">
                                        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-slate-300 mb-3 shadow-sm"><FaUser /></div>
                                        <p className="text-sm font-bold text-slate-400">Searching for workers...</p>
                                        <Link to={`/manage-job/${job.id}`} className="mt-3 text-emerald-600 text-sm font-bold hover:underline">View Applicants</Link>
                                    </div>
                                )}
                            </div>

                        </div>
                        
                        {/* Footer Action */}
                        <div className="mt-6 pt-4 border-t border-slate-50 flex justify-end">
                            <Link to={`/manage-job/${job.id}`} className="text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors flex items-center">
                                Manage Job & Hires &rarr;
                            </Link>
                        </div>
                    </div>
                );
            })}
          </div>
      )}

      <RatingModal 
        isOpen={isRatingModalOpen} 
        onClose={() => setIsRatingModalOpen(false)} 
        workerName={ratingData.workerName}
        workerId={ratingData.workerId}
        hireId={ratingData.hireId}
        onSuccess={fetchJobs} 
      />
    </div>
  );
};

export default MyJobsPage;